import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { debounce, pick, snakeCase, uniqBy } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import AppliedFiltersChips, {
  AppliedFiltersChipsCollectedData,
} from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS } from '~constants/consts';
import { SiteSelectedFilter } from '~constants/enums';
import {
  OrderDispatchQuery,
  useDepartmentsQuery,
  useDummyQuery,
} from '~pages/Dispatch/components/filters/orders-dispatch-filters-queries/OrdersDispatchFiltersQueries';
import useSitesFilters, {
  getVisibilityOptions,
  sitesFilterKeyBySelectedFilter,
} from '~pages/Settings/Administration/Sites/hooks/useSitesFilters';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const dummyQueryFilters = [SiteSelectedFilter.SITE_TYPES, SiteSelectedFilter.VISIBILITY];
const noneOptionFilters = [SiteSelectedFilter.DEPARTMENTS];
const SiteFilters = observer(() => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<SiteSelectedFilter>();
  const { userStore, companyAssetsStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const {
    availableOptions,
    appliedFilterChipsCollectedData,
    updateAvailableOptions,
    updateAppliedFilterChipsCollectedData,
  } = useSitesFilters();

  const [searchQuery, setSearchQuery] = useState<{ input: string; debounced: string }>({
    input: '',
    debounced: '',
  });

  const debouncedSetSearchQuery = useRef(
    debounce((value: string) => {
      setSearchQuery((state) => ({ ...state, debounced: value }));
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const visibilityQuery = useDummyQuery(getVisibilityOptions());
  const siteTypesQuery = useDummyQuery(availableOptions.siteTypes);
  const departmentsQuery = useDepartmentsQuery(companyId);

  const activeQuery = useMemo(() => {
    if (!selectedFilter) {
      return null;
    }

    const queries: Record<SiteSelectedFilter, OrderDispatchQuery> = {
      [SiteSelectedFilter.SITE_TYPES]: siteTypesQuery,
      [SiteSelectedFilter.DEPARTMENTS]: departmentsQuery,
      [SiteSelectedFilter.VISIBILITY]: visibilityQuery,
    };

    return queries[selectedFilter];
  }, [selectedFilter, siteTypesQuery, departmentsQuery, visibilityQuery]);

  const isDummyQuery = useMemo(() => {
    return selectedFilter && dummyQueryFilters.includes(selectedFilter);
  }, [selectedFilter]);

  const searchValueChangeCallback = useMemo(() => {
    if (isDummyQuery) {
      return undefined;
    }

    return (value: string) => setSearchQuery((state) => ({ ...state, input: value }));
  }, [isDummyQuery]);

  const filterChipsData = useMemo(() => {
    const appliedFilters = pick(
      companyAssetsStore.siteFilters,
      Object.values(sitesFilterKeyBySelectedFilter),
    );

    const collectedData = Object.entries(appliedFilterChipsCollectedData).reduce(
      (obj, [key, collectedValues]) => {
        const filter = sitesFilterKeyBySelectedFilter[key as SiteSelectedFilter];

        obj[filter] = collectedValues;

        return obj;
      },
      {} as AppliedFiltersChipsCollectedData,
    );

    return { appliedFilters, collectedData };
  }, [JSON.stringify(companyAssetsStore.siteFilters), appliedFilterChipsCollectedData]);

  const hasFiltersApplied = useMemo(() => {
    const { appliedFilters } = filterChipsData;

    return Object.values(appliedFilters).filter((value) => Boolean(value)).length > 0;
  }, [JSON.stringify(filterChipsData.appliedFilters)]);

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = sitesFilterKeyBySelectedFilter[selectedFilter];
    const options = companyAssetsStore.siteFilters[key];

    return options ?? [];
  }, [companyAssetsStore.siteFilters, selectedFilter]);

  const handleSelectedOptionsChange = useCallback(
    (selectedOptions: string[]) => {
      if (selectedFilter) {
        const key = sitesFilterKeyBySelectedFilter[selectedFilter];
        const options = selectedOptions.length > 0 ? selectedOptions : undefined;

        companyAssetsStore.setSiteFilters({ [key]: options }, true);
      }
    },
    [selectedFilter],
  );

  const fetchMoreCallback = useMemo(() => {
    return activeQuery?.fetchMore;
  }, [activeQuery?.fetchMore]);

  const handleRemoveAllFilters = useCallback((filters: string | string[]) => {
    const filtersArray = Array.isArray(filters) ? filters : [filters];
    const filtersObject = Object.fromEntries(
      filtersArray.map((filter) => [filter, undefined]),
    );

    companyAssetsStore.setSiteFilters(filtersObject, true);
  }, []);

  const updateAvailableOptionsAndFilterChips = useCallback(
    (key: SiteSelectedFilter, data: FilterMenuOption[]) => {
      updateAvailableOptions((state) => {
        return { ...state, [key]: uniqBy([...state[key], ...data], (i) => i.value) };
      });

      updateAppliedFilterChipsCollectedData(key, data);
    },
    [],
  );

  const getNoneOptionLabel = (selectedFilter: SiteSelectedFilter) => {
    if (noneOptionFilters.includes(selectedFilter)) {
      return `${t('dispatch.dispatch_v2.filters.no_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`;
    }
  };

  useEffect(() => {
    activeQuery?.fetch(searchQuery.debounced);
  }, [activeQuery?.fetch, searchQuery.debounced]);

  useEffect(() => {
    if (!isDummyQuery && selectedFilter && activeQuery?.data) {
      updateAvailableOptionsAndFilterChips(selectedFilter, activeQuery?.data);
    }
  }, [
    updateAvailableOptionsAndFilterChips,
    selectedFilter,
    activeQuery?.data,
    isDummyQuery,
  ]);

  useEffect(() => {
    debouncedSetSearchQuery(searchQuery.input);

    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery, searchQuery.input]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(undefined);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedFilter) {
      departmentsQuery.clear();
    }
  }, [selectedFilter, departmentsQuery.clear]);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={{
              [`&.${buttonClasses.root}`]: {
                backgroundColor: theme.semantic.surfaceRaised,
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                ...(hasFiltersApplied
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            }}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            focusSearchFieldOnMount
            searchValue={searchQuery.input}
            onSearchValueChange={searchValueChangeCallback}
            loadingReason={activeQuery?.loadingReason}
            selectAllOptionLabel={`${t('dispatch.dispatch_v2.filters.all_entities', { entity: t(`dispatch.dispatch_v2.filters.${snakeCase(selectedFilter)}`) })}`}
            options={activeQuery?.data ?? []}
            selectedOptions={selectedOptions}
            onSelectedOptionsChange={handleSelectedOptionsChange}
            onFetchMore={fetchMoreCallback}
            selectNoneOptionLabel={getNoneOptionLabel(selectedFilter)}
          />
        )}
        {!selectedFilter &&
          Object.values(SiteSelectedFilter).map((filter) => (
            <MenuItem key={filter} onClick={() => setSelectedFilter(filter)}>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography color={theme.brandV2.colors.treadBlack} variant="subtitle2">
                  {t(`dispatch.dispatch_v2.filters.${snakeCase(filter)}`)}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {(
                    companyAssetsStore.siteFilters[
                      sitesFilterKeyBySelectedFilter[filter]
                    ] ?? []
                  ).length > 0 && (
                    <Check
                      sx={{ fontSize: '16px', color: theme.brandV2.colors.treadOrange }}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>
      <AppliedFiltersChips
        {...filterChipsData}
        onRemoveFilter={handleRemoveAllFilters}
        onRemoveAllFilters={handleRemoveAllFilters}
      />
    </Box>
  );
});

export default SiteFilters;
